/**
 * Icon with Background Component
 * Reusable icon container with consistent styling
 */

import React, { useState } from 'react';
import Image from 'next/image';
import { typographyColors, spacing, borders } from '@/design-system/utils';

export interface IconWithBackgroundProps {
  icon: React.ReactNode | string | null;
  iconSize?: number;
  alt?: string;
  className?: string;
}

// Default fallback icon when image fails to load
const FallbackIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 20 20'
    fill='none'
    style={{ flexShrink: 0 }}
  >
    <circle
      cx='10'
      cy='10'
      r='7'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

export const IconWithBackground: React.FC<IconWithBackgroundProps> = ({
  icon,
  iconSize = 20,
  alt = '',
  className,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  // Extract numeric value from spacing for calculations (spacing[2] = '8px')
  const paddingValue: number = 8;

  // Reset error state when icon changes
  React.useEffect(() => {
    if (
      typeof icon === 'string' &&
      (icon.startsWith('http') || icon.startsWith('/'))
    ) {
      setImageError(false);
      setImageLoading(true);
    } else {
      setImageLoading(false);
    }
  }, [icon]);

  // Determine what to render
  const renderIcon = () => {
    // If icon is null or undefined, show fallback
    if (!icon) {
      return <FallbackIcon size={iconSize} />;
    }

    // If icon is a string URL, try to render as image
    if (
      typeof icon === 'string' &&
      (icon.startsWith('http') || icon.startsWith('/'))
    ) {
      // If image failed to load, show fallback
      if (imageError) {
        return <FallbackIcon size={iconSize} />;
      }

      // Render image with error handling
      return (
        <div
          style={{
            width: `${iconSize}px`,
            height: `${iconSize}px`,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {imageLoading && (
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.3,
              }}
            >
              <FallbackIcon size={iconSize * 0.6} />
            </div>
          )}
          <Image
            src={icon}
            alt={alt}
            fill
            style={{ objectFit: 'contain' }}
            onError={() => {
              console.warn(
                '❌ [IconWithBackground] Image failed to load:',
                icon
              );
              setImageError(true);
              setImageLoading(false);
            }}
            onLoad={() => {
              setImageLoading(false);
            }}
            onLoadingComplete={() => {
              setImageLoading(false);
            }}
            unoptimized={true} // Use unoptimized to avoid Next.js image optimization issues
          />
        </div>
      );
    }

    // If icon is a React component, render it in a container with proper sizing
    // React components from react-icons need explicit sizing
    return (
      <div
        style={{
          width: `${iconSize}px`,
          height: `${iconSize}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: 'inherit',
        }}
      >
        {React.isValidElement(icon)
          ? React.cloneElement(
              icon as React.ReactElement<{ size?: number; color?: string }>,
              {
                size: iconSize,
                color: 'currentColor',
              }
            )
          : icon}
      </div>
    );
  };

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing[2],
        borderRadius: borders.radius.lg,
        backgroundColor: '#F8F9F8',
        padding: spacing[2],
        flexShrink: 0,
        color: typographyColors.primary,
        justifyContent: 'center',
        width: `${iconSize + paddingValue * 2}px`,
        height: `${iconSize + paddingValue * 2}px`,
      }}
    >
      {renderIcon()}
    </div>
  );
};
